import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  VpnAttendanceSliceStateType,
  VPNQueryBody,
  ReadVPNExcelFileType,
  IncomingVpnAttendanceList,
} from '../../../types/TimeAndAttendance/VpnAttendance/VpnAttendanceTypes'
import VPNAttendanceApi from '../../../middleware/api/TimeAndAttendance/VpnAttendance/vpnAttendanceApi'
import { emptyString } from '../../../constant/constantData'
import { RootState } from '../../../stateStore'
import { initialPageSize } from '../../../pages/TimeAndAttendance/VpnAttendance/VpnConstants'

const VpnQueryInitial: VPNQueryBody = {
  search: emptyString,
  dateSelection: emptyString,
  startIndex: 0,
  endIndex: 0,
  from: undefined,
  to: undefined,
}

const ReadVPNExcelFile = createAsyncThunk(
  'vpnAttendance/readVPNExcelFile',
  async (file: FormData, thunkApi) => {
    try {
      return await VPNAttendanceApi.readExcelFile(file)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getVPNAttendance = createAsyncThunk(
  'vpnAttendance/getVPNAttendence',
  async (query: VPNQueryBody, thunkApi) => {
    try {
      // const data = await VPNAttendanceApi.getVPNAttendance(outBody)
      // return thunkApi.fulfillWithValue(data)
      return await VPNAttendanceApi.getVPNAttendance(query)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialVPNAttendanceState: VpnAttendanceSliceStateType = {
  isLoading: ApiLoadingState.idle,
  error: null,
  listSize: 0,
  excelData: [],
  vpnAttendanceList: { list: [], size: 0 },
  vpnQuery: VpnQueryInitial,
  selectedDate: '',
  fromDate: '',
  toDate: '',
  searchString: '',
}

const vpnAttendanceSlice = createSlice({
  name: 'vpnAttendance',
  initialState: initialVPNAttendanceState,
  reducers: {
    clearVpnAttendance: (state) => {
      state.listSize = initialPageSize
      state.vpnAttendanceList = { size: 0, list: [] }
    },
    setVPNQuery: (state, action) => {
      state.vpnQuery = action.payload
    },
    clearVPNQuery: (state) => {
      state.vpnQuery = VpnQueryInitial
    },
    setDateValue: (state, action) => {
      state.selectedDate = action.payload
    },
    setFromDateFilter: (state, action) => {
      state.fromDate = action.payload as string
    },
    setToDateFilter: (state, action) => {
      state.toDate = action.payload as string
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ReadVPNExcelFile.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.excelData = action.payload
    })
    builder.addCase(getVPNAttendance.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.vpnAttendanceList = action.payload
    })
    builder.addMatcher(isAnyOf(ReadVPNExcelFile.fulfilled), (state) => {
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(isAnyOf(ReadVPNExcelFile.pending), (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addMatcher(isAnyOf(ReadVPNExcelFile.rejected), (state, action) => {
      state.isLoading = ApiLoadingState.failed
      state.error = action.payload as ValidationError
    })
    builder.addMatcher(isAnyOf(getVPNAttendance.fulfilled), (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.listSize = action.payload.size
    })
    builder.addMatcher(isAnyOf(getVPNAttendance.pending), (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addMatcher(isAnyOf(getVPNAttendance.rejected), (state, action) => {
      state.isLoading = ApiLoadingState.failed
      state.error = action.payload as ValidationError
    })
  },
})

export const VpnAttendanceThunk = {
  ReadVPNExcelFile,
  getVPNAttendance,
}

const isLoading = (state: RootState): ApiLoadingState =>
  state.vpnAttendance.isLoading

const excelData = (state: RootState): ReadVPNExcelFileType[] =>
  state.vpnAttendance.excelData

const vpnList = (state: RootState): IncomingVpnAttendanceList['list'] =>
  state.vpnAttendance.vpnAttendanceList?.list

const getSelectedDateValue = (state: RootState): string =>
  state.vpnAttendance.selectedDate

const getFromDateFilter = (state: RootState): string | Date =>
  state.vpnAttendance.fromDate

const getToDateFilter = (state: RootState): string | Date =>
  state.vpnAttendance.toDate

const getSearchString = (state: RootState): string | Date =>
  state.vpnAttendance.searchString

const listSize = (state: RootState): number => state.vpnAttendance.listSize

export const vpnAttendanceSelectors = {
  isLoading,
  excelData,
  vpnList,
  getSelectedDateValue,
  getFromDateFilter,
  getToDateFilter,
  listSize,
  getSearchString,
}

export const vpnAttendanceService = {
  ...VpnAttendanceThunk,
  actions: vpnAttendanceSlice.actions,
  selectors: vpnAttendanceSelectors,
}

export default vpnAttendanceSlice.reducer
